import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import CustomersPage from "@/pages/CustomersPage";
import InspectionsPage from "@/pages/InspectionsPage";
import LoansPage from "@/pages/LoansPage";
import FinancePage from "@/pages/FinancePage";
import SettingsPage from "@/pages/SettingsPage";
import HRModulePage from "@/pages/HRModulePage";
import NotificationConfigPage from "@/pages/NotificationConfigPage";
import AdAnalyticsPage from "@/pages/AdAnalyticsPage";
import MonitorPage from "@/pages/ErrorLogsPage";
import InspectionReportPage from "@/pages/InspectionReportPage";
import CustomerReportPage from "@/pages/CustomerReportPage";
import PreviewReportPage from "@/pages/PreviewReportPage";
import UnifiedReportPage from "@/pages/UnifiedReportPage";
import MetaOAuthCallback from "@/pages/MetaOAuthCallback";
import MechanicAppTestPage from "@/pages/MechanicAppTestPage";
import AdminPasswordReset from "@/pages/AdminPasswordReset";
import SystemDiagnostics from "@/pages/SystemDiagnostics";
import SheerdriveReportPreview from "@/pages/SheerdriveReportPreview";
import SheerdriveReport from "@/pages/SheerdriveReport";
import PdfDebugPage from "@/pages/PdfDebugPage";
import PaymentReconcile from "@/pages/PaymentReconcile";
import DealersPage from "@/pages/DealersPage";
import ObdSdkDashboard from "@/pages/ObdSdkDashboard";
import MfcwlDashboard from "@/pages/MfcwlDashboard";
import MfcwlPublicReportPage from "@/pages/MfcwlPublicReportPage";

// Smart redirect based on visible tabs
const SmartRedirect = () => {
  const { visibleTabs, loading } = useAuth();
  
  if (loading) return null;
  
  // Map tab names to routes - Admin merged into HR
  const tabRouteMap = {
    dashboard: '/dashboard',
    leads: '/leads',
    customers: '/customers',
    inspections: '/inspections',
    loans: '/loans',
    'ad-analytics': '/ad-analytics',
    hr: '/hr',
    finance: '/finance',
    settings: '/settings',
  };
  
  // Find the first visible tab and redirect there
  if (visibleTabs && visibleTabs.length > 0) {
    const firstTab = visibleTabs[0];
    const route = tabRouteMap[firstTab] || '/leads';
    return <Navigate to={route} replace />;
  }
  
  return <Navigate to="/leads" replace />;
};

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Meta OAuth Callback - Public route for popup */}
          <Route path="/meta-oauth-callback" element={<MetaOAuthCallback />} />
          
          {/* Mechanic App Test Page - Debug time formatting */}
          <Route path="/mechanic-test" element={<MechanicAppTestPage />} />
          
          {/* Admin Password Reset - Only works when REACT_APP_ENABLE_PASSWORD_RESET=true */}
          <Route path="/admin/reset-password" element={<AdminPasswordReset />} />
          
          {/* System Diagnostics - Check backend health and connectivity */}
          <Route path="/admin/diagnostics" element={<SystemDiagnostics />} />
          
          {/* UNIFIED REPORT PAGE - Single source of truth for customer views */}
          {/* Customer Report Access - Public route with OTP verification */}
          <Route path="/r/:id" element={<UnifiedReportPage />} />
          <Route path="/report/:id" element={<UnifiedReportPage />} />
          <Route path="/portal/:id" element={<UnifiedReportPage />} />
          
          {/* Admin Preview Report - Uses PreviewReportPage (battle-tested) */}
          <Route path="/preview-report/:id" element={<PreviewReportPage />} />
          
          {/* Legacy routes for backward compatibility */}
          <Route path="/inspection-report/:id" element={<InspectionReportPage />} />
          <Route path="/inspection-report" element={<InspectionReportPage />} />
          
          {/* Sheerdrive Report Preview - Public sample view */}
          <Route path="/sheerdrive-report-preview" element={<SheerdriveReportPreview />} />
          {/* Sheerdrive Report - Dynamic per inspection */}
          <Route path="/sheerdrive-report/:inspectionId" element={<SheerdriveReport />} />

          {/* MFCWL public tokenized web report */}
          <Route path="/mfcwl/report/:id" element={<MfcwlPublicReportPage />} />
          
          {/* Protected Routes */}
          <Route element={<MainLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/dealers" element={<DealersPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/inspections" element={<InspectionsPage />} />
            <Route path="/loans" element={<LoansPage />} />
            <Route path="/ad-analytics" element={<AdAnalyticsPage />} />
            <Route path="/hr" element={<HRModulePage />} />
            <Route path="/hr/notifications" element={<Navigate to="/system-monitor" replace />} />
            <Route path="/finance" element={<FinancePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/system-monitor" element={<MonitorPage />} />
            <Route path="/settings/notifications" element={<Navigate to="/system-monitor" replace />} />
            <Route path="/admin/pdf-debug" element={<PdfDebugPage />} />
            <Route path="/admin/payment-reconcile" element={<PaymentReconcile />} />
            <Route path="/obd-sdk" element={<ObdSdkDashboard />} />
            <Route path="/mfcwl" element={<MfcwlDashboard />} />
            {/* Redirect old /admin to /hr */}
            <Route path="/admin" element={<Navigate to="/hr" replace />} />
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<SmartRedirect />} />
          <Route path="*" element={<SmartRedirect />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
